import { apiRequest, safeApiRequest, setStoredTokens, getStoredToken, getStoredRefreshToken, getApiBaseUrl, setStoredUser, refreshToken, isAccessTokenFresh } from './apiClient';
import { buildWebDeviceInfo } from '../utils/deviceInfo';

const CSRF_TOKEN_KEY = 'hlm_csrf_token';
const CSRF_TOKEN_EXPIRY = 'hlm_csrf_token_expiry';

/**
 * Cookie'den CSRF token alır (XSRF-TOKEN cookie'si)
 */
const getCsrfTokenFromCookie = () => {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'XSRF-TOKEN' && value) {
        return decodeURIComponent(value);
      }
    }
  } catch (error) {
    console.error('Cookie\'den CSRF token okunurken hata:', error);
  }
  return null;
};

/**
 * CSRF Token'ı localStorage'dan veya cookie'den alır
 * Token yoksa veya süresi dolmamışsa yeni token alır
 */
export const getCsrfToken = async () => {
  try {
    // Önce cookie'den kontrol et (dokümantasyona göre cookie olarak da gelebilir)
    const cookieToken = getCsrfTokenFromCookie();
    if (cookieToken) {
      // Cookie'den alınan token'ı localStorage'a da kaydet
      const expiryTime = Date.now() + 30 * 60 * 1000;
      localStorage.setItem(CSRF_TOKEN_KEY, cookieToken);
      localStorage.setItem(CSRF_TOKEN_EXPIRY, expiryTime.toString());
      return cookieToken;
    }

    // localStorage'dan kontrol et
    const storedToken = localStorage.getItem(CSRF_TOKEN_KEY);
    const expiry = localStorage.getItem(CSRF_TOKEN_EXPIRY);
    
    // Token varsa ve süresi dolmamışsa mevcut token'ı döndür
    if (storedToken && expiry && Date.now() < parseInt(expiry, 10)) {
      return storedToken;
    }

    // Yeni CSRF token al (dokümantasyona göre: GET /api/auth/csrf-token)
    try {
      // Timeout ekle (10 saniye - CSRF token için daha kısa)
      const timeoutMs = 10000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      let response;
      try {
        response = await fetch(`${getApiBaseUrl()}/api/auth/csrf-token`, {
          method: 'GET',
          credentials: 'include', // Cookie'ler için gerekli (XSRF-TOKEN cookie'si için)
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.warn('CSRF token isteği zaman aşımına uğradı');
          return null;
        }
        throw fetchError;
      }

      // Network hatası kontrolü
      if (!response || response.type === 'error' || response.status === 0) {
        console.error('CSRF token alınamadı: Sunucuya bağlanılamadı');
        return null;
      }

      if (!response.ok) {
        // 404 ise endpoint henüz yapılmamış olabilir, sessizce devam et
        if (response.status === 404) {
          console.warn('CSRF token endpoint bulunamadı (404). Endpoint henüz backend\'de yapılmamış olabilir.');
          return null;
        }
        console.error(`CSRF token alınamadı: HTTP ${response.status}`);
        return null;
      }

      const data = await response.json().catch(() => {
        console.error('CSRF token yanıtı parse edilemedi');
        return null;
      });

      if (!data) {
        return null;
      }

      // Response'dan CSRF token al: { basarili: true, veri: { csrfToken: "..." } }
      const csrfToken = data?.veri?.csrfToken || data?.csrfToken;

      // Cookie'den de kontrol et (Set-Cookie header'dan gelebilir)
      const cookieTokenAfterRequest = getCsrfTokenFromCookie();
      const finalToken = csrfToken || cookieTokenAfterRequest;

      if (!finalToken) {
        console.error('Geçersiz CSRF token yanıtı');
        return null;
      }

      // Token'ı localStorage'a kaydet (30 dakika geçerli)
      const expiryTime = Date.now() + 30 * 60 * 1000;
      localStorage.setItem(CSRF_TOKEN_KEY, finalToken);
      localStorage.setItem(CSRF_TOKEN_EXPIRY, expiryTime.toString());

      return finalToken;
    } catch (error) {
      console.error('CSRF token alınırken hata:', error);
      // Network hatası ise detaylı log
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('API sunucusuna bağlanılamıyor:', getApiBaseUrl());
      }
      return null;
    }
  } catch (error) {
    console.error('CSRF token okunurken hata:', error);
    return null;
  }
};

/**
 * CSRF Token'ı temizler
 */
export const clearCsrfToken = () => {
  try {
    localStorage.removeItem(CSRF_TOKEN_KEY);
    localStorage.removeItem(CSRF_TOKEN_EXPIRY);
  } catch (error) {
    console.error('CSRF token temizlenirken hata:', error);
  }
};

/**
 * Login işlemi
 * @param {string} username - Kullanıcı adı
 * @param {string} password - Kullanıcı şifresi
 * @returns {Promise<Object>} - Login sonucu
 */
export const login = async (username, password) => {
  // Input validasyonu
  if (!username || !password) {
    throw new Error('Kullanıcı adı ve şifre gereklidir');
  }

  // Backend dokümantasyonuna göre:
  // - kullaniciAdi: Herhangi bir karakter kısıtlaması YOK, sadece boş olamaz
  // - sifre: Login'de format kontrolü YOK, sadece boş olamaz (format kontrolü sadece register'da)
  const trimmedUsername = username.trim();
  
  // Backend validation: kullaniciAdi boş olamaz (başka kısıtlama yok)
  if (!trimmedUsername || trimmedUsername.length === 0) {
    throw new Error('Kullanıcı adı boş olamaz');
  }

  // Backend validation: sifre boş olamaz (login'de format kontrolü yok)
  const trimmedPassword = String(password).trim();
  
  if (!trimmedPassword || trimmedPassword.length === 0) {
    throw new Error('Şifre boş olamaz');
  }
  
  // Sanitization yapmıyoruz - backend herhangi bir karakter kısıtlaması istemiyor
  const sanitizedUsername = trimmedUsername;
  const sanitizedPassword = trimmedPassword;
  
  console.log('[AuthService] Sanitized credentials:', {
    originalUsername: trimmedUsername,
    sanitizedUsername,
    passwordLength: sanitizedPassword.length,
  });

  try {
    // CSRF token al (dokümantasyona göre POST isteklerinde kullanılmalı)
    await getCsrfToken();

    // Login endpoint: POST /api/auth/giris
    // Backend dokümantasyonuna göre: kullaniciAdi ve sifre (Türkçe field isimleri)
    // Content-Type: application/json header'ı mutlaka olmalı
    const requestBody = {
      kullaniciAdi: sanitizedUsername,
      sifre: sanitizedPassword,
      deviceInfo: buildWebDeviceInfo(),
    };
    
    console.log('[AuthService] Login request body:', {
      kullaniciAdi: requestBody.kullaniciAdi,
      kullaniciAdiLength: requestBody.kullaniciAdi?.length,
      sifreLength: requestBody.sifre?.length,
      sifreType: typeof requestBody.sifre,
      bodyStringified: JSON.stringify(requestBody),
    });
    
    const result = await apiRequest('/api/auth/giris', {
      method: 'POST',
      body: requestBody, // apiClient.js JSON.stringify yapacak ve Content-Type header'ı ekleyecek
      // CSRF token header'da otomatik gönderilecek (X-CSRF-Token)
      skipCsrf: false,
    });

    // Response formatı: { basarili, mesaj, veri: { kullanici, token, refreshToken } }
    console.log('[AuthService] Login response (raw):', JSON.stringify(result, null, 2));
    console.log('[AuthService] Login response basarili:', result?.basarili);
    console.log('[AuthService] Login response mesaj:', result?.mesaj);
    console.log('[AuthService] Login response veri:', result?.veri);
    console.log('[AuthService] Login response hatalar:', result?.hatalar);
    
    // basarili false ise veya undefined ise hata var
    if (result?.basarili === false || (!result?.basarili && result?.mesaj)) {
      // Başarısız login - backend'den gelen hata mesajını fırlat
      // Backend dokümantasyonuna göre: doğrulama hatası response'unda hatalar array'i VAR
      let errorMessage = result?.mesaj || result?.message || result?.error || 'Giriş başarısız';
      
      // Backend'den gelen hatalar array'ini kontrol et (format: [{alan: "body.kullaniciAdi", mesaj: "..."}])
      if (result?.hatalar && Array.isArray(result.hatalar) && result.hatalar.length > 0) {
        // Validation hatası - hatalar array'inden ilk hatayı veya tümünü göster
        console.error('[AuthService] Validation errors:', result.hatalar);
        
        // İlk hatanın mesajını kullan (daha kullanıcı dostu)
        const firstError = result.hatalar[0];
        errorMessage = firstError.mesaj || firstError.message || errorMessage;
        
        // VEYA tüm hataları birleştir (opsiyonel)
        // const hataMesajlari = result.hatalar.map(h => h.mesaj || h.message).filter(Boolean).join(', ');
        // errorMessage = hataMesajlari || errorMessage;
      }
      
      console.error('[AuthService] Login failed:', errorMessage);
      console.error('[AuthService] Full response:', JSON.stringify(result, null, 2));
      throw new Error(errorMessage);
    }
    
    // Token kontrolü - basarili true olsa da token yoksa hata olabilir
    if (!result?.veri?.token) {
      console.error('[AuthService] No token in response even though basarili is true:', result);
      const errorMsg = result?.mesaj || 'Token alınamadı. Lütfen tekrar deneyin.';
      throw new Error(errorMsg);
    }
    
    // Token'ları kaydet
    setStoredTokens({
      token: result.veri.token,
      refreshToken: result.veri.refreshToken,
      sessionId: result.veri.sessionId,
    });
    console.log('[AuthService] Tokens saved successfully');

    // Kullanıcı bilgisini de localStorage'a kaydet
    const userData = result?.veri?.kullanici || null;
    if (userData) {
      setStoredUser(userData);
      console.log('[AuthService] User data saved to localStorage:', userData);
    }

    return {
      success: true,
      basarili: true,
      user: userData,
      mesaj: result?.mesaj || 'Giriş başarılı',
    };
  } catch (error) {
    // Hata durumunda token'ları temizle
    setStoredTokens({ token: null, refreshToken: null });
    throw error;
  }
};

/**
 * Logout işlemi
 */
export const logout = async () => {
  try {
    const token = getStoredToken();
    const refreshTokenValue = getStoredRefreshToken();
    if (token) {
      try {
        await apiRequest('/api/auth/cikis', {
          method: 'POST',
          body: refreshTokenValue ? { refreshToken: refreshTokenValue } : {},
        });
      } catch (error) {
        console.error('Çıkış hatası:', error);
      }
    }
  } catch (error) {
    console.error('Çıkış hatası:', error);
  } finally {
    setStoredTokens({ token: null, refreshToken: null, sessionId: null });
    clearCsrfToken();
  }
};

/** Sayfa yükü / sekme görünür olduğunda oturumu yenile. */
export const bootstrapAuthSession = async () => {
  const refreshTokenValue = getStoredRefreshToken();
  if (!refreshTokenValue) {
    const err = new Error('Refresh token yok');
    err.status = 401;
    throw err;
  }
  if (!isAccessTokenFresh()) {
    await refreshToken();
  }
  return fetchUserProfile();
};

/**
 * Kullanıcı profil bilgisini getirir
 */
export const fetchUserProfile = async () => {
  // Access token süresi dolmuşsa önce refresh token ile yenile, sonra tekrar dene (AuthContext'in gereksiz çıkışı önler)
  return safeApiRequest('/api/auth/profil', {
    method: 'GET',
    skipCsrf: true,
  });
};
